import { Suspense, lazy, useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AppLayout } from "./AppLayout";
import { LandingPage } from "./pages/Landing";
import { ConsentGate, ConsentRedirector } from "./components/ConsentGate";
import { PageLoading } from "./components/Skeleton";

// แยกแต่ละหน้าเป็น chunk ของตัวเอง (Landing โหลดทันทีเพราะเป็นหน้าแรก) ลดขนาดที่ต้องโหลดตอนเปิดแอป
const AccountPage = lazy(() => import("./pages/Account").then((m) => ({ default: m.AccountPage })));
const CapturePage = lazy(() => import("./pages/Capture").then((m) => ({ default: m.CapturePage })));
const CheckPage = lazy(() => import("./pages/Check").then((m) => ({ default: m.CheckPage })));
const EstimatingPage = lazy(() => import("./pages/Estimating").then((m) => ({ default: m.EstimatingPage })));
const JobDetailPage = lazy(() => import("./pages/JobDetail").then((m) => ({ default: m.JobDetailPage })));
const JobsPage = lazy(() => import("./pages/Jobs").then((m) => ({ default: m.JobsPage })));
const ComingSoonPage = lazy(() => import("./pages/ComingSoon").then((m) => ({ default: m.ComingSoonPage })));
const LoginPage = lazy(() => import("./pages/Login").then((m) => ({ default: m.LoginPage })));
const CheckoutPage = lazy(() => import("./pages/Checkout").then((m) => ({ default: m.CheckoutPage })));
const PricingPage = lazy(() => import("./pages/Pricing").then((m) => ({ default: m.PricingPage })));
const AdminPage = lazy(() => import("./pages/admin/AdminPage").then((m) => ({ default: m.AdminPage })));
const ResultPage = lazy(() => import("./pages/Result").then((m) => ({ default: m.ResultPage })));
const SavedResultPage = lazy(() => import("./pages/Result").then((m) => ({ default: m.SavedResultPage })));
const ConsentPage = lazy(() => import("./pages/Consent").then((m) => ({ default: m.ConsentPage })));
const PrivacyPage = lazy(() => import("./pages/Privacy").then((m) => ({ default: m.PrivacyPage })));
const PoliciesPage = lazy(() => import("./pages/Policies").then((m) => ({ default: m.PoliciesPage })));
const QuotationPage = lazy(() => import("./pages/Quotation").then((m) => ({ default: m.QuotationPage })));
const QuotationCompanyPage = lazy(() => import("./pages/QuotationCompany").then((m) => ({ default: m.QuotationCompanyPage })));
const ShareViewPage = lazy(() => import("./pages/ShareView").then((m) => ({ default: m.ShareViewPage })));

/** เปลี่ยนหน้าแล้วเลื่อนกลับบนสุด (หน้ารายละเอียดงานกับผลประเมินยาวต่างกัน) */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    try {
      window.scrollTo(0, 0);
    } catch {
      /* ไม่รองรับ */
    }
  }, [pathname]);
  return null;
}

export function App() {
  return (
    <>
      <ScrollToTop />
      <ConsentRedirector />
      <Suspense fallback={<PageLoading />}>
      <Routes>
      <Route path="/capture" element={<CapturePage />} />
      <Route path="/check" element={<ConsentGate><CheckPage /></ConsentGate>} />
      <Route path="/estimating/:id" element={<EstimatingPage />} />
      <Route path="/result/:id" element={<ResultPage />} />
      <Route path="/jobs/:id/estimate" element={<SavedResultPage />} />
      {/* หน้าสาธารณะอ่านอย่างเดียว ไม่ต้องเข้าสู่ระบบ ไม่มี AppBar/BottomTabBar */}
      <Route path="/s/:token" element={<ShareViewPage />} />
      <Route element={<AppLayout />}>
        <Route index element={<LandingPage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="consent" element={<ConsentPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="policies" element={<PoliciesPage />} />
        <Route path="admin" element={<AdminPage />} />
        <Route path="jobs" element={<JobsPage />} />
        <Route path="jobs/:id" element={<JobDetailPage />} />
        <Route path="jobs/:id/quote" element={<QuotationPage />} />
        <Route path="account/company" element={<QuotationCompanyPage />} />
        {/* ส่วนที่เหลือของ flow จะทำใน Phase ถัดไป */}
        <Route path="pricing" element={<PricingPage />} />
        <Route path="checkout/:plan" element={<CheckoutPage />} />
        {/* S07 เป็น Sheet ที่เปิดจากจุดที่โควตาหมด ไม่มีหน้าแยก: ลิงก์ตรงไปหน้าราคา */}
        <Route path="paywall" element={<Navigate to="/pricing" replace />} />
        <Route path="checkout" element={<Navigate to="/pricing" replace />} />
        {["quotation"].map((p) => (
          <Route key={p} path={p} element={<ComingSoonPage />} />
        ))}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
      </Routes>
      </Suspense>
    </>
  );
}
